import { useEffect, useState } from 'react';
import { Button, ButtonGroup, Chip, Grid, LinearProgress, Stack, TextField, Typography } from '@mui/material';
//@ts-ignore
import { Autocomplete } from '@mui/material';
import { OrderType } from 'enum/order-type';
import { IStock } from 'types/custom/stock';
import { InputLabel } from '@mui/material';
import { placeOrderValidationSchemaV2 } from 'validations/placeOrderValidationSchema';
import { Form, FormikProvider, useFormik } from 'formik';
import { FormHelperText } from '@mui/material';
import serverCall from 'api/serverCall';
import api from 'api/api';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import AlertOrderConfirmation from './AlertOrderConfirmation';
import { TransactionType } from 'enum/transaction-type';
import { calculateOrderPlacedCount, getActiveUsersCountFromSelected } from 'utils/custom';
import OrderPlacedAlert from 'components/alert/OrderPlacedAlert';
import { useDataContext } from 'contexts/DataContext';
import { Limit } from 'types/custom/common';
import { MarketQuote } from 'types/custom/market';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import { Tick } from 'types/custom/websocket';
import _ from 'lodash';
import ViewUsersModal from './VewUsersModal';

interface Props {
  selectedStock: IStock | null;
  setSelectedStock: (val: IStock | null) => void;
  numberOfStocks: number | string;
  setNumberOfStocks: (val: number | string) => void;
  selectedUsers: Record<string, any>[] | null;
  setNoUsersActive: (val: boolean) => void;
  handleCloseModal: () => void;
  setFormik: (val: any) => void;
  limit: Limit | null;
  setLimit: (val: Limit | null) => void;
  currentTick: Tick | null;
  setCurrentTick: (val: Tick | null) => void;
  totalLoggedInUsers: number;
}
type InitialValues = {
  stock: string | null;
  price: string;
  orderType: null | string;
  quantity: string;
  triggerPrice: string | number;
  submit: any;
};
const getInitialValues = () => {
  const newOrder: InitialValues = {
    stock: null,
    price: '',
    orderType: OrderType.LIMIT,
    quantity: '',
    triggerPrice: '',
    submit: null
  };
  return newOrder;
};

type AlertData = {
  open: boolean;
  status: 'success' | 'failed' | '';
  total: number;
  success: number;
  failed: number;
};

const PlaceOrderCard = ({
  selectedStock,
  setSelectedStock,
  selectedUsers,
  setNoUsersActive,
  handleCloseModal,
  setFormik,
  currentTick,
  setCurrentTick,
  limit,
  setLimit,
  totalLoggedInUsers
}: Props) => {
  const [isSlEnabled, setIsSlEnabled] = useState<boolean>(false);
  const [transactionType, setTransactionType] = useState<TransactionType | null>(null);
  const [orderAlertState, setOrderAlertState] = useState<AlertData>({ open: false, status: '', total: 0, success: 0, failed: 0 });
  const [confirmationState, setConfirmationState] = useState<{
    isOpen: boolean;
    isConfirmed: boolean;
  }>({ isOpen: false, isConfirmed: false });
  const [marketQuoteLoading, setMarketQuoteLoading] = useState<boolean>(false);
  const [viewUsersModalOpen, setViewUsersModalOpen] = useState<boolean>(false);
  const { stocks } = useDataContext();
  const { liveTicks } = useWebsocketContext();
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: placeOrderValidationSchemaV2(isSlEnabled, transactionType, limit),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!confirmationState.isConfirmed) {
        setConfirmationState({ isOpen: true, isConfirmed: false });
        return;
      }
      try {
        const anyUserLoggedIn = selectedUsers && selectedUsers.some((user) => user.loggedIn);
        if (!anyUserLoggedIn) {
          handleCloseModal();
          setNoUsersActive(true);
          resetForm();
          return;
        }

        if (confirmationState.isConfirmed) {
          const requestBody = selectedUsers.map((user) => ({
            userId: user.userId,
            quantity: values.quantity,
            stockId: selectedStock?.stockId,
            transactionType: transactionType,
            price: values.price,
            orderType: values.orderType,
            validity: 'DAY',
            product: 'CNC',
            triggerPrice: values.orderType === OrderType.LIMIT ? values.price : values.triggerPrice
          }));
          serverCall.post(api.placeOrder(), requestBody, (response: any, error: any) => {
            if (error) {
              console.error(error);
              const errorMessage = error?.response?.data.message as string;
              setSubmitting(false);
              dispatch(
                openSnackbar({
                  open: true,
                  close: true,
                  message: errorMessage || `Failed to place order`,
                  variant: 'alert',
                  alert: {
                    color: 'error'
                  },
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                  }
                })
              );
            } else {
              const totalOrders = response?.orderList.length;
              if (response?.orderList?.length === 0) {
                setOrderAlertState({ open: true, status: 'failed', total: totalOrders, success: 0, failed: 0 });
              } else {
                const orderList = response?.orderList;
                const result = calculateOrderPlacedCount(orderList);
                setOrderAlertState({ open: true, status: 'success', total: totalOrders, success: result.success, failed: result.failed });
              }
            }
          });
          setSubmitting(false);
          resetForm();
          setSelectedStock(null);
          setConfirmationState({ isOpen: false, isConfirmed: false });
        }
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue, values, handleChange } = formik;

  useEffect(() => {
    if (liveTicks && selectedStock?.stockId) {
      const tick = liveTicks.current[selectedStock?.stockId];
      setCurrentTick(tick);
    }
  }, [liveTicks, selectedStock]);

  useEffect(() => {
    if (confirmationState.isOpen) {
      handleCloseModal();
    }
  }, [confirmationState.isOpen]);

  useEffect(() => {
    if (selectedStock) {
      setFieldValue('stock', selectedStock.stockName);
      getPriceCircuit(selectedStock.stockId);
      subscribeToWatchList(selectedStock.stockId);
    }
  }, [selectedStock]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (formik) {
        setFormik(formik);
      }
    }, 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, []);

  const getPriceCircuit = (stockId: string) => {
    setMarketQuoteLoading(true);
    serverCall.get(api.getMarketQuote(stockId), (response: MarketQuote, error: any) => {
      if (error) {
        setMarketQuoteLoading(false);
        console.error(error);
      } else {
        const limit = {
          upperLimit: response.upperCircuitLimit,
          lowerLimit: response.lowerCircuitLimit,
          lastPrice: response.lastPrice,
          stockId
        };
        setLimit(limit);
        // setFieldTouched('price', undefined);
        // setFieldError('price', undefined);
        setTimeout(() => {
          setFieldValue('price', limit.lastPrice);
        }, 100);
        setMarketQuoteLoading(false);
      }
    });
  };

  const subscribeToWatchList = (stockId: string) => {
    serverCall.put(api.subscribeToWatchList(stockId), {}, (response, error) => {
      if (error) {
        console.error(error);
      } else {
      }
    });
  };

  const handleConfirmation = async () => {
    setConfirmationState({ isOpen: false, isConfirmed: true });
    // await new Promise((resolve) => setTimeout(resolve, 200));
    formik.submitForm();
  };

  const handleCloseOrderPlacedAlert = () => {
    setOrderAlertState({ open: false, status: '', total: 0, success: 0, failed: 0 });
  };

  const handleClose = () => {
    setConfirmationState({ isOpen: false, isConfirmed: false });
    // formik.resetForm();
  };
  const handleOnChangeAutoComplete = _.debounce((event: any, newValue: any) => {
    const selectedStock = stocks.find((stock) => stock.stockName === newValue?.stockName);
    setFieldValue('stock', newValue);
    if (selectedStock) {
      setSelectedStock(selectedStock);
    }
  }, 300);

  const filterOptions = (options: IStock[], state: { inputValue: string }) => {
    const inputValueLower = state?.inputValue?.toLowerCase();
    return options?.filter(
      (option) =>
        option?.stockName?.toLowerCase()?.startsWith(inputValueLower) || option?.stockId?.toLowerCase()?.startsWith(inputValueLower)
    );
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          {confirmationState.isOpen && (
            <AlertOrderConfirmation
              transactionType={transactionType}
              handleClose={handleClose}
              open={confirmationState.isOpen}
              stock={selectedStock!}
              quantity={parseFloat(values.quantity) || 0}
              stockPrice={parseFloat(values.price) || 0}
              users={getActiveUsersCountFromSelected(selectedUsers) ?? 0}
              handleConfirm={handleConfirmation}
            />
          )}

          {orderAlertState.open && (
            <OrderPlacedAlert
              open={orderAlertState.open}
              handleClose={handleCloseOrderPlacedAlert}
              status={orderAlertState.status}
              totalOrders={orderAlertState.total}
              successFulOrders={orderAlertState.success}
            />
          )}
          {viewUsersModalOpen && (
            <ViewUsersModal
              open={viewUsersModalOpen}
              setOpen={setViewUsersModalOpen}
              users={selectedUsers ? selectedUsers?.filter((u) => u?.loggedIn) : null}
            />
          )}
          <Grid container spacing={2}>
            {(currentTick || limit?.lastPrice) && (
              <Grid item xs={12} md={12} sx={{ margin: 0, padding: 0 }}>
                <Chip
                  size="small"
                  label={
                    <Typography color={'#808080'} variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {` NSE - ${limit?.stockId} :`}
                      {marketQuoteLoading ? (
                        <LinearProgress color="primary" style={{ width: '2rem' }} />
                      ) : (
                        <span style={{ marginLeft: '2px' }}>{currentTick?.price ?? limit?.lastPrice}</span>
                      )}
                    </Typography>
                  }
                  sx={{ borderRadius: 1, p: 0, backgroundColor: 'transparent', mt: -1, ml: -0.9 }}
                />
              </Grid>
            )}
            {selectedUsers && selectedUsers.length > 0 && (
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="h5" color="secondary" sx={{ textDecoration: 'none' }}>
                    Selected {selectedUsers.length} users, out of which {totalLoggedInUsers} {totalLoggedInUsers === 1 ? 'is' : 'are'}{' '}
                    logged in
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setViewUsersModalOpen(true);
                    }}
                  >
                    View
                  </Button>
                </Stack>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="stock-label" sx={{ mb: 1 }}>
                Stock
              </InputLabel>
              <Autocomplete
                sx={{
                  '& .MuiOutlinedInput-root': {
                    p: 1
                  },
                  '& .MuiAutocomplete-tag': {
                    bgcolor: 'primary.lighter',
                    border: '1px solid',
                    zIndex: 10000,
                    borderColor: 'primary.light',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark'
                      }
                    }
                  }
                }}
                size="medium"
                disablePortal
                fullWidth
                id="basic-autocomplete-label"
                options={stocks}
                filterOptions={filterOptions}
                getOptionLabel={(option) => option?.stockName || ''}
                value={selectedStock || null}
                onChange={handleOnChangeAutoComplete}
                renderOption={(props, option, { index }) => (
                  <li {...props} key={index}>
                    {option?.stockName} ({option?.stockId})
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select stock"
                    inputProps={{
                      ...params.inputProps
                    }}
                  />
                )}
                noOptionsText="No stocks found"
                ListboxProps={{
                  style: {
                    maxHeight: '165px',
                    overflow: 'auto'
                  }
                }}
              />
              {touched.stock && errors.stock && (
                <FormHelperText error id="standard-weight-helper-text-stock-place-order">
                  {errors.stock}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="order-type-label" sx={{ mb: 1 }}>
                Order Type
              </InputLabel>
              <ButtonGroup fullWidth sx={{ height: 48 }} variant="outlined" aria-label="Loading button group">
                <Button
                  variant={values.orderType === OrderType.LIMIT ? 'contained' : 'outlined'}
                  onClick={() => setFieldValue('orderType', OrderType.LIMIT)}
                >
                  Regular
                </Button>
                {/* <Button
                  variant={values.orderType === OrderType.AMO ? 'contained' : 'outlined'}
                  onClick={() => setFieldValue('orderType', OrderType.AMO)}
                >
                  AMO
                </Button> */}
                <Button
                  variant={values.orderType === OrderType.SL ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFieldValue('orderType', OrderType.SL);
                    setIsSlEnabled(true);
                  }}
                >
                  Stop Loss
                </Button>
              </ButtonGroup>
              {touched.orderType && errors.orderType && (
                <FormHelperText error id="standard-weight-helper-text-order-type-place-order">
                  {errors.orderType}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="price-label" sx={{ mb: 1 }}>
                Price
              </InputLabel>
              <TextField
                inputProps={{ min: 0, step: 0.1 }}
                id="outlined-number"
                placeholder="Price"
                type="number"
                {...getFieldProps('price')}
                onChange={handleChange}
                fullWidth
              />
              {touched.price && errors.price && (
                <FormHelperText error id="standard-weight-helper-text-price-place-order">
                  {errors.price}
                </FormHelperText>
              )}
            </Grid>
            {values.orderType === OrderType.SL && (
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="trigger-price-label" sx={{ mb: 1 }}>
                  Trigger Price
                </InputLabel>
                <TextField
                  inputProps={{ min: 1 }}
                  id="outlined-number"
                  placeholder="Trigger Price"
                  type="number"
                  {...getFieldProps('triggerPrice')}
                  onChange={handleChange}
                  fullWidth
                />
                {touched.triggerPrice && errors.triggerPrice && (
                  <FormHelperText error id="standard-weight-helper-text-trigger-price-place-order">
                    {errors.triggerPrice}
                  </FormHelperText>
                )}
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="quantity-label" sx={{ mb: 1 }}>
                Quantity
              </InputLabel>
              <TextField
                inputProps={{ min: 1 }}
                id="outlined-number"
                placeholder="Quantity"
                type="number"
                {...getFieldProps('quantity')}
                onChange={handleChange}
                fullWidth
              />
              {touched.quantity && errors.quantity && (
                <FormHelperText error id="standard-weight-helper-text-quantity-place-order">
                  {errors.quantity}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setTransactionType(TransactionType.BUY);
                  }}
                  size="large"
                  sx={{ mr: 2 }}
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  BUY
                </Button>
                <Button
                  onClick={() => {
                    setTransactionType(TransactionType.SELL);
                  }}
                  size="large"
                  type="submit"
                  variant="contained"
                  color="error"
                  disabled={isSubmitting}
                >
                  SELL
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </>
  );
};

export default PlaceOrderCard;
